/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */



$( document ).ready(function() {
  $('.single-slider .slide-list').slick({
    dots: false,
    infinite: true,
    speed: 300,
    slidesToShow: 1,
    arrows: true,
    slidesToScroll: 1,
    adaptiveHeight: true
  });

  $('.collection-listing .product-list').slick({
    dots: false,
    arrows: true,
    infinite: false,
    speed: 300,
    slidesToShow: 5,
    slidesToScroll: 1,
    responsive: [
   {
      breakpoint: 1360,
      settings: {
        slidesToShow: 4,
        slidesToScroll: 1,
        infinite: true,
        dots: false
      }
    },
    {
      breakpoint: 1200,
      settings: {
        slidesToShow: 3,
        slidesToScroll: 1,
        infinite: true,
        dots: false
      }
    },
    {
      breakpoint: 820,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1
      }
    },
    {
      breakpoint: 575,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1
      }
    }
  ]
})

$('.reviews-bg .customer-list').slick({
    dots: false,
    arrows: true,
    infinite: false,
    speed: 300,
    slidesToShow: 3,
    slidesToScroll: 1,
    responsive: [
    {
      breakpoint: 1024,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1
      }
    },
    {
      breakpoint: 767,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1
      }
    }
  ]
})

$('.section-main-product .custom-thumbs').slick({
    dots: false,
    arrows: true,
    infinite: false,
    speed: 300,
    slidesToShow: 5,
    slidesToScroll: 1,
    responsive: [
    {
      breakpoint: 1360,
      settings: {
        slidesToShow: 4,
        slidesToScroll: 1
      }
    },
    {
      breakpoint: 768,
      settings: {
        slidesToShow: 3,
        slidesToScroll: 1
      }
    },
    {
      breakpoint: 480,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1
      }
    }
  ]
})

// $(function(){
//   var $modal = $('.embed-container');
//   var HIDE_CLASS = 'is-hide';
  
//   $('#center-img-main').on('click',function(){
//     $modal.removeClass(HIDE_CLASS);
//   });
  
//   $('.js-modal-close').on('click',function(){
//     $modal.addClass(HIDE_CLASS);
//   });
// });


});


$(".select_tabs").click(function() {
  $(this).toggleClass("open");
  });
  $(".select_tabs li").click(function() {
  
    var selected_value = $(this).html();
    var first_li = $(".select_tabs li:first-child").html();
  
    $(".select_tabs li:first-child").html(selected_value);
    $(this).html(first_li);
  
  });
  
  $(document).mouseup(function(event) {
    var target = event.target;
    var select = $(".select_tabs");
    if (!select.is(target) && select.has(target).length === 0) {
      select.removeClass("open");
    }

});



// tabbed content
// http://www.entheosweb.com/tutorials/css/tabs.asp
$(".tab-container .tab_content").hide();
// $(".tab-container .tab_content:first").show();
$('.tab-container').each(function(k,v){
  $(v).find('.tab_content:first').show();
});


/* if in tab mode */
$("ul.tabs li").click(function() {
  var parent = $(this).parents('.tab-container');

  $(parent).find(".tab_content").hide();
  var activeTab = $(this).attr("rel");
  $(parent).find("#"+activeTab).fadeIn();

  $(parent).find("ul.tabs li").removeClass("active");
  $(this).addClass("active");

  $(parent).find(".tab_drawer_heading").removeClass("d_active");
  $(parent).find(".tab_drawer_heading[rel^='"+activeTab+"']").addClass("d_active");

  $('.product-list').slick('refresh');
});

/* if in drawer mode */
$(".tab_drawer_heading").click(function() {
  var parent = $(this).parents('.tab-container');

  $(parent).find(".tab_content").hide();
  var d_activeTab = $(this).attr("rel");
  $(parent).find("#"+d_activeTab).fadeIn();

  $(parent).find(".tab_drawer_heading").removeClass("d_active");
  $(this).addClass("d_active");

  $(parent).find("ul.tabs li").removeClass("active");
  $(parent).find("ul.tabs li[rel^='"+d_activeTab+"']").addClass("active");

  $('.product-list').slick('refresh');
});











// =============================================================================
// Desktop tier-2 → tier-3 expand on CLICK (replaces hover)
//
// Scope: .main-nav-header (desktop <main-navigation>) only. Mobile drawer is
// untouched because it lives outside .main-nav-header.
// Trigger: click on the .custom-subnav parent row's .navigation__column-title.
// Effect: toggles .subnav-click-open on the .custom-subnav <li>. The matching
// CSS rule in custom.css (inside @media min-width:768px) reveals tier-3.
// Leaf links inside tier-2 and tier-3 are NOT intercepted — they navigate as
// normal.
// =============================================================================
(function () {
  function isDesktop() {
    return window.matchMedia('(min-width: 768px)').matches;
  }

  function closeAllSubnavs(except) {
    document.querySelectorAll('.main-nav-header .custom-subnav.subnav-click-open').forEach(function (el) {
      if (el !== except) el.classList.remove('subnav-click-open');
    });
  }

  // Delegated click on the tier-2 expandable parent title.
  document.addEventListener('click', function (e) {
    if (!isDesktop()) return;

    // The chevron is inside the <a.navigation__column-title>, so any click
    // on text or chevron matches this single selector.
    var titleLink = e.target.closest('.main-nav-header .custom-subnav > .navigation__column-title');
    if (!titleLink) return;

    var subnav = titleLink.parentElement; // the .custom-subnav <li>
    if (!subnav) return;

    e.preventDefault();

    var wasOpen = subnav.classList.contains('subnav-click-open');
    closeAllSubnavs(wasOpen ? null : subnav);
    subnav.classList.toggle('subnav-click-open', !wasOpen);
  }, false);

  // Close any open subnavs when the user clicks outside the desktop nav.
  document.addEventListener('click', function (e) {
    if (!isDesktop()) return;
    if (!e.target.closest('.main-nav-header')) closeAllSubnavs(null);
  }, true);

  // When the cursor leaves the whole tier-2 panel, reset tier-3 state so the
  // next open of this tier-1 dropdown starts collapsed. IMPORTANT: attach the
  // listener directly to each tier-2-container — do NOT use capture-phase
  // delegation, because mouseleave on descendants (moving from the tier-2
  // parent row into the expanded tier-3 list) would otherwise fire here and
  // collapse the submenu the user just opened.
  function wireTier2LeaveHandlers(root) {
    root.querySelectorAll('.main-nav-header .navigation__tier-2-container').forEach(function (panel) {
      if (panel.dataset.subnavLeaveBound === '1') return;
      panel.dataset.subnavLeaveBound = '1';
      panel.addEventListener('mouseleave', function () {
        if (!isDesktop()) return;
        closeAllSubnavs(null);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { wireTier2LeaveHandlers(document); });
  } else {
    wireTier2LeaveHandlers(document);
  }
})();

// =============================================================================
// Desktop tier-1 dropdown on CLICK (replaces hover)
//
// Scope: .main-nav-header (desktop <main-navigation>) only.
// Strategy: capture-phase listeners swallow mouseenter/mouseleave on T1 items
// so the theme's hover handlers never fire. Click on the T1 link toggles the
// dropdown via the same .navigation__item--show-children class the theme uses.
// =============================================================================
(function () {
  function isDesktop() {
    return window.matchMedia('(min-width: 768px)').matches;
  }

  var NAV_SELECTOR = '.main-nav-header';
  var T1_ITEM = '.navigation__tier-1 > .navigation__item--with-children';

  // --- Block hover -----------------------------------------------------------
  // Capture-phase so we intercept before the theme's bubble-phase listeners.
  function blockHover(e) {
    if (!isDesktop()) return;
    var item = e.target.closest(NAV_SELECTOR + ' ' + T1_ITEM);
    if (item) {
      e.stopPropagation();
      // Also clear any pending open/close timeouts the theme may have set
      clearTimeout(item.dataset.navOpenTimeoutId);
      clearTimeout(item.dataset.navCloseTimeoutId);
    }
  }
  document.addEventListener('mouseenter', blockHover, true);
  document.addEventListener('mouseleave', blockHover, true);

  // --- Helpers ---------------------------------------------------------------
  function closeAllT1(except) {
    document.querySelectorAll(NAV_SELECTOR + ' ' + T1_ITEM + '.navigation__item--show-children').forEach(function (el) {
      if (el !== except) {
        el.classList.remove('navigation__item--show-children');
        el.firstElementChild.setAttribute('aria-expanded', 'false');
      }
    });
    // Also remove the header-level "nav open" state
    var header = document.querySelector('.section-header');
    if (header && !document.querySelector(NAV_SELECTOR + ' .navigation__item--show-children')) {
      header.classList.remove('section-header--nav-open', 'section-header--nav-hover');
    }
  }

  // --- Click to toggle -------------------------------------------------------
  document.addEventListener('click', function (e) {
    if (!isDesktop()) return;

    var link = e.target.closest(NAV_SELECTOR + ' ' + T1_ITEM + ' > .navigation__link');
    if (!link) return;

    e.preventDefault();
    var item = link.parentElement;
    var wasOpen = item.classList.contains('navigation__item--show-children');

    closeAllT1(wasOpen ? null : item);

    if (!wasOpen) {
      item.classList.add('navigation__item--show-children');
      link.setAttribute('aria-expanded', 'true');
      item.closest('.section-header').classList.add('section-header--nav-open');
    } else {
      item.classList.remove('navigation__item--show-children');
      link.setAttribute('aria-expanded', 'false');
    }
  }, false);

  // --- Click outside to close ------------------------------------------------
  document.addEventListener('click', function (e) {
    if (!isDesktop()) return;
    if (!e.target.closest(NAV_SELECTOR)) {
      closeAllT1(null);
    }
  }, true);

  // --- Escape key to close ---------------------------------------------------
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeAllT1(null);
  });
})();
